import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Combobox } from '@bb/ui';
import type { AdminApi } from '../../api/client.ts';

interface Props {
  environmentId: string;
  label?: string;
  value: string | null;
  onChange: (name: string | null) => void;
}

/**
 * Event choice for trigger composers (BB-27 → BB-100/BB-111/BB-117): name, description, status, properties and an
 * example payload, so a marketer picks the right event without asking a developer. Blocked events never appear.
 */
export function EventPicker({ environmentId, label = 'Event', value, onChange }: Props) {
  const { api } = useRouter().options.context as { api: AdminApi };
  // The registry is small (VCG: ~80 events): load it once and let the combobox filter.
  const items = useQuery({ queryKey: ['event-picker', environmentId], queryFn: () => api.eventPicker(environmentId, '') });
  const selected = items.data?.find((e) => e.name === value) ?? null;
  const options = (items.data ?? []).map((e) => ({ value: e.name, label: e.deprecated ? `${e.name} (deprecated)` : e.name }));

  return (
    <div className="event-picker">
      <Combobox label={label} options={options} value={value} onChange={onChange} placeholder="Search events" emptyText={items.isPending ? 'Loading events…' : 'No events'} />
      {selected && (
        <section className="event-picker__details" aria-label={`About ${selected.name}`}>
          <p>{selected.description ?? <span className="hint">No description yet.</span>}</p>
          {selected.deprecated && <p className="notice notice--warning">Deprecated: prefer its replacement in new campaigns.</p>}
          {selected.properties.length > 0 && (
            <ul aria-label="Properties">
              {selected.properties.map((p) => (
                <li key={p.path}>
                  <code>{p.path}</code> <span className="hint">{p.dataType}</span>
                </li>
              ))}
            </ul>
          )}
          {selected.examplePayload && (
            <details>
              <summary>Example payload</summary>
              <pre>{JSON.stringify(selected.examplePayload, null, 2)}</pre>
            </details>
          )}
        </section>
      )}
    </div>
  );
}
